import { Intent, MenuItem, Spinner, Tooltip } from "@blueprintjs/core"
import { ItemPredicate, ItemRenderer, Suggest } from "@blueprintjs/select"
import { useEffect, useRef, useState } from "react"

import { ToastError } from "../../../components"
import { useDebounce } from "../../../hooks/useDebounce"
import AddNewTickerDialog from "../components/AddNewTickerDialog"
import {
  AddTickerButton,
  DetailCard,
  MainContainer,
  SearchContainer,
} from "../components/CommonStyled.styled"
import useTradingInstrument from "../hooks/useTradingInstrument"
import useTradingInstruments from "../hooks/useTradingInstruments"
import { InstrumentLight } from "../types"
import InstrumentForm from "./InstrumentForm"

const Instrument = () => {
  const [selectedInstrumentId, setSelectedInstrumentId] = useState<number | undefined>(undefined)
  const [query, setQuery] = useState<string>("")
  const [isOpenDialog, setIsOpenDialog] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  // Fetch full instrument details when an instrument is selected
  const { instrument: selectedInstrument, isLoading: isLoadingDetail } = useTradingInstrument({
    instrumentId: selectedInstrumentId,
  })

  // Search for instruments
  const debouncedSetQuery = useDebounce((newQuery: string) => {
    setQuery(newQuery)
  }, 500)

  const { instruments, isLoading, error } = useTradingInstruments({ query })

  useEffect(() => {
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }, 0)
  }, [])

  // Handler to select an instrument from the search results
  const handleSelectInstrument = (instrument: InstrumentLight) => {
    setSelectedInstrumentId(instrument.id)
  }

  const renderInstrument: ItemRenderer<InstrumentLight> = (item, { handleClick, modifiers }) => {
    if (!modifiers.matchesPredicate) return null
    return (
      <MenuItem
        active={modifiers.active}
        key={item.id}
        text={item.bloomberg_code}
        onClick={handleClick}
      />
    )
  }

  const filterInstruments: ItemPredicate<InstrumentLight> = queryStr => {
    if (queryStr.length < 2) return false
    return true // Server already filters
  }

  return (
    <MainContainer flexDirection="column" alignItems="center" justifyContent="center" gap="30px">
      <SearchContainer flexDirection="row" alignItems="center" gap="10px">
        <Suggest
          items={instruments || []}
          itemRenderer={renderInstrument}
          itemPredicate={filterInstruments}
          onItemSelect={handleSelectInstrument}
          noResults={
            <MenuItem
              disabled
              text={
                query.length < 2
                  ? "Start typing to search..."
                  : isLoading
                    ? "Loading..."
                    : "No instruments found"
              }
            />
          }
          inputValueRenderer={item => item.bloomberg_code}
          inputProps={{
            placeholder: "Search by ticker...",
            large: true,
            inputRef: inputRef,
          }}
          query={query}
          onQueryChange={debouncedSetQuery}
          resetOnSelect
          resetOnClose
          fill
          disabled={!!error}
        />
        <Tooltip content="Add new ticker(s)">
          <AddTickerButton
            onClick={() => setIsOpenDialog(true)}
            icon="plus"
            intent={Intent.PRIMARY}
            minimal
            outlined
          />
        </Tooltip>
        <AddNewTickerDialog
          isOpenDialog={isOpenDialog}
          setIsOpenDialog={setIsOpenDialog}
          title="Add new ticker(s)"
        />
        {error && (
          <ToastError
            error={error}
            errorMessage="Unable to search instruments, please try to refresh the page."
          />
        )}
      </SearchContainer>

      {selectedInstrumentId && isLoadingDetail && <Spinner />}

      {selectedInstrument && (
        <DetailCard>
          <InstrumentForm
            selectedInstrument={selectedInstrument}
            setSelectedInstrumentId={setSelectedInstrumentId}
          />
        </DetailCard>
      )}
    </MainContainer>
  )
}

export default Instrument
